"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Briefcase, Compass, Handshake, ScrollText } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const principles = [{
  title: "Relationship-based support",
  description: "We take time to understand each client’s operating reality, including governance pressures, reporting backlogs, staffing gaps, and the demands of working in Northern communities.",
  icon: Handshake
}, {
  title: "Practical financial leadership",
  description: "We help clients strengthen financial information, improve reporting, support sound decisions, and move important work forward with clear follow-through.",
  icon: Briefcase
}, {
  title: "Solutions that fit the client",
  description: "We do not force templates onto the work. We build practical systems, reporting tools, and support structures that fit the client’s goals, capacity, and priorities.",
  icon: Compass
}];
export default function OurStory() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="19262" id="about-riverbend" ref={sectionRef} className="bg-background text-foreground py-16 md:py-20 overflow-hidden relative">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(53,128,121,0.10),transparent_38%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(17,47,75,0.06),transparent_42%)] pointer-events-none" />

      <div className={`max-w-7xl mx-auto px-6 lg:px-8 relative z-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="max-w-5xl mb-12 md:mb-14">
          <p className="text-secondary font-default font-bold tracking-widest uppercase text-sm mb-4">
            About RiverBend
          </p>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold tracking-tight text-primary-500 leading-tight mb-6">
            Practical, relationship-based support
            <span className="block text-secondary">
              built for Northern realities.
            </span>
          </h1>

          <p className="text-lg md:text-xl text-muted-foreground font-default leading-relaxed max-w-4xl">
            RiverBend helps organizations make sense of complex financial,
            operational, and reporting challenges through hands-on support,
            clear systems, and dependable follow-through.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 xl:gap-12 items-center mb-14 md:mb-20">
          <div className="lg:col-span-7 xl:col-span-8">
            <Card className="border-border shadow-sm rounded-2xl overflow-hidden bg-card">
              <CardContent className="p-8 md:p-10 lg:p-12">
                <div className="flex items-center gap-4 mb-6">
                  <div className="h-px w-12 bg-secondary" />
                  <span className="text-sm font-default font-bold uppercase tracking-widest text-muted-foreground">
                    Established 2009
                  </span>
                </div>

                <div className="space-y-5 text-base md:text-lg text-muted-foreground font-default leading-relaxed">
                  <p>
                    RiverBend Accounting &amp; Management Services Professional
                    Corporation was established in 2009 to help organizations
                    address complex financial and operational challenges in a
                    practical, respectful, and sustainable way.
                  </p>

                  <p>
                    What began as a focused accounting practice has grown into
                    broader advisory and management support for First Nation
                    communities, community-owned entities, Northern
                    organizations, and businesses that need dependable financial
                    leadership, clear reporting, and stronger systems.
                  </p>

                  <p>
                    Today, RiverBend provides First Nation advisory services,
                    controllership, year-end preparation, financial recovery
                    support, reporting systems, and project-based financial and
                    administrative support. We work alongside leadership and
                    staff to build stability, clarity, and long-term capacity in
                    ways that reflect real operating conditions across the North.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-5 xl:col-span-4">
            <div className="relative group">
              <div className="absolute inset-0 border border-secondary/25 translate-x-4 translate-y-4 rounded-2xl -z-10 transition-transform duration-700 group-hover:translate-x-2 group-hover:translate-y-2" />

              <div className="relative overflow-hidden rounded-2xl shadow-xl border border-border bg-muted">
                <img src="https://stable-woodcock.10web.cloud/wp-content/uploads/2026/04/ChatGPT-Image-Apr-21-2026-11_41_30-AM.png" alt="Northern Ontario waterfront" className="w-full h-full object-cover aspect-[4/3] transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 xl:gap-12 items-center mb-14 md:mb-20">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative overflow-hidden rounded-2xl shadow-xl border border-border bg-muted">
              <img src="https://stable-woodcock.10web.cloud/wp-content/uploads/2026/04/ChatGPT-Image-Apr-21-2026-11_50_52-AM.png" alt="Jon Schiffer, Executive Consultant and CEO" className="w-full h-full object-cover aspect-[4/3]" loading="lazy" />

              <div className="absolute bottom-4 left-4 right-4 bg-background/90 backdrop-blur-sm rounded-md px-4 py-3 border border-border">
                <p className="text-xs font-default font-semibold text-primary-500 uppercase tracking-wider">
                  Jon Schiffer, Executive Consultant &amp; CEO
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 order-1 lg:order-2">
            <p className="text-secondary font-default font-bold tracking-widest uppercase text-sm mb-4">
              How We Work
            </p>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-primary-500 mb-6 leading-tight">
              We work alongside clients to move important work forward.
            </h2>

            <div className="space-y-5 text-base md:text-lg text-muted-foreground font-default leading-relaxed">
              <p>
                Our approach is rooted in relationships, practical
                problem-solving, and steady follow-through. We take the time to
                understand each client’s operating realities, whether that means
                governance pressures, reporting backlogs, staffing gaps, or the
                day-to-day demands of working in Northern communities.
              </p>

              <p>
                We work collaboratively with leadership, managers, finance
                staff, and program teams to strengthen systems, improve
                financial information, and support sound decision-making. Our
                role is not just to advise, but to help move work forward in a
                way that is realistic, respectful, and sustainable.
              </p>

              <p>
                RiverBend values clarity, professionalism, and accountability.
                We believe good support starts with listening carefully,
                communicating plainly, and building solutions that fit the
                client rather than forcing a template onto the work.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 xl:gap-8 mb-14 md:mb-20">
          {principles.map((item, index) => {
          const Icon = item.icon;
          return <Card key={item.title} data-index={index} className="h-full border-border shadow-sm hover:shadow-md hover:border-secondary/40 transition-all rounded-lg bg-card">
                <CardContent className="p-7 md:p-8">
                  <div className="size-12 rounded-md bg-secondary/10 flex items-center justify-center mb-5">
                    <Icon className="size-6 text-secondary" />
                  </div>

                  <h3 className="text-xl font-default font-bold text-primary-500 mb-3">
                    {item.title}
                  </h3>

                  <p className="text-muted-foreground font-default leading-relaxed">
                    {item.description}
                  </p>
                </CardContent>
              </Card>;
        })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 xl:gap-10 mb-14 md:mb-20">
          <Card className="border-border shadow-sm rounded-lg bg-secondary/5">
            <CardContent className="p-8 md:p-10">
              <div className="flex items-center gap-3 mb-5">
                <Briefcase className="size-6 text-secondary" />
                <h3 className="text-2xl md:text-3xl font-default font-bold text-primary-500">
                  Mission
                </h3>
              </div>

              <div className="space-y-4 text-muted-foreground font-default leading-relaxed text-base md:text-lg">
                <p>
                  RiverBend supports organizations through growth and change by
                  providing trusted, practical guidance.
                </p>

                <p>
                  We work alongside clients through a people-centred,
                  collaborative approach to build strong financial systems, lead
                  complex initiatives, and explore emerging technologies where
                  they support the client’s own goals and priorities.
                </p>

                <p>
                  Through respectful collaboration, professional care, and a
                  commitment to dependability, integrity, and accuracy, we help
                  organizations build stability and capacity in ways that
                  reflect who they are and the future they are working toward.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border shadow-sm rounded-lg bg-secondary/5">
            <CardContent className="p-8 md:p-10">
              <div className="flex items-center gap-3 mb-5">
                <Compass className="size-6 text-secondary" />
                <h3 className="text-2xl md:text-3xl font-default font-bold text-primary-500">
                  Vision
                </h3>
              </div>

              <div className="space-y-4 text-muted-foreground font-default leading-relaxed text-base md:text-lg">
                <p>
                  RiverBend’s vision is to be the collaborative partner that
                  organizations turn to as their needs evolve.
                </p>

                <p>
                  We are committed to deepening our expertise in respectful
                  advisory and project management, strengthening financial
                  reporting and organizational information, and thoughtfully
                  using modern tools, automation, and emerging technologies to
                  give clients stronger communication and reporting support.
                </p>

                <p>
                  Our goal is to help clients feel equipped and confident to
                  shape their futures.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="max-w-5xl mx-auto text-center rounded-2xl border border-secondary/25 bg-card shadow-md p-8 md:p-12 lg:p-14">
          <ScrollText className="size-10 text-secondary mx-auto mb-6" />

          <h3 className="text-3xl md:text-4xl font-default font-bold text-primary-500 mb-5 leading-tight">
            Built to support stability, capacity, and long-term progress.
          </h3>

          <p className="text-lg text-muted-foreground mb-8 font-default leading-relaxed max-w-3xl mx-auto">
            RiverBend helps clients make sense of complex financial and
            operational challenges through hands-on support, clearer systems,
            and dependable follow-through. Our work is designed to improve
            reporting, support leadership, strengthen administration, and help
            organizations move forward with greater confidence.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="h-12 px-8 rounded-md bg-secondary hover:bg-secondary/90 text-white font-default">
              <Link to="/services">
                Explore Our Services
                <Briefcase className="ml-2 size-4" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="h-12 px-8 rounded-md border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-white font-default">
              <Link to="/contact">
                Contact RiverBend
                <ArrowRight className="ml-2 size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}